"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import type { TenantSettingsDto } from "@/types/tenant";

const CONSENT_VALUE = "accepted";

export function CookieConsent({ tenant }: { tenant: TenantSettingsDto }) {
  const settings = tenant.settings;
  const analyticsId = typeof settings.analyticsId === "string" ? settings.analyticsId : "";
  const enabled = Boolean(settings.cookieConsentEnabled);
  const storageKey = `cookie-consent:${tenant.slug}`;
  const [accepted, setAccepted] = useState(!enabled);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem(storageKey);
    setAccepted(!enabled || stored === CONSENT_VALUE);
    setReady(true);
  }, [enabled, storageKey]);

  useEffect(() => {
    if (!accepted || !analyticsId.startsWith("G-") || document.querySelector(`script[data-analytics-id="${analyticsId}"]`)) {
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;
    script.dataset.analyticsId = analyticsId;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", analyticsId);
  }, [accepted, analyticsId]);

  if (!enabled || accepted || !ready) return null;

  const accept = () => {
    window.localStorage.setItem(storageKey, CONSENT_VALUE);
    setAccepted(true);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-lg">
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-[var(--color-text)]">
          {settings.cookieConsentText || "We use analytics cookies to understand site traffic."}{" "}
          <Link href="/privacy" className="font-medium text-[var(--color-primary)] underline">
            Privacy policy
          </Link>
        </p>
        <button
          type="button"
          onClick={accept}
          className="min-h-[44px] rounded-[var(--border-radius)] bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white"
        >
          Accept
        </button>
      </div>
    </div>
  );
}

declare global {
  interface Window {
    dataLayer: unknown[][];
    gtag: (...args: unknown[]) => void;
  }
}
